import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import FilterListIcon from '@material-ui/icons/FilterList';
import SwapVertIcon from '@material-ui/icons/SwapVert';
import cn from 'classnames';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Form from 'raydiant-elements/core/Form';
import AlertIcon from 'raydiant-elements/core/AlertIcon';
import InputLabel from 'raydiant-elements/core/InputLabel';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import SwapVertReverseIcon from 'raydiant-elements/icons/SwapVertReverse';
import ActionBar from 'raydiant-elements/core/ActionBar/v2';
import Row from 'raydiant-elements/layout/Row';
import Spacer from 'raydiant-elements/layout/Spacer';
import Column from 'raydiant-elements/layout/Column';
import { makeStyles, createStyles } from 'raydiant-elements/styles';
import { textTruncate, buttonReset } from 'raydiant-elements/mixins';
import { Theme } from 'raydiant-elements/theme';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Profile,
  Resource,
  ResourceACL,
  ResourceProfile,
} from '@raydiant/api-client-js';
import { selectOtherDomainProfiles } from '../../selectors/v2/domains';
import Popover from '../Popover';
import {
  domainRoleLabels,
  canEditResource,
  SortProfileOptions,
} from '../../utilities';
import {
  selectSharingSortOptions,
  selectSharingSearchQuery,
  selectSharingProfileResults,
} from './selectors';
import * as actions from './actions';
import useCurrentUser from '../../hooks/useCurrentUser';

interface ShareResourceProps {
  resource: Resource;
  resourceType: 'screen' | 'theme';
  onShare: (profileId: string) => Promise<void>;
  onUnshare: (resourceACL: ResourceACL) => Promise<void>;
}

export const filterStandardAndRestrictedUsers = (
  profile: ResourceProfile,
  currentUser: Profile | undefined,
) => {
  if (currentUser?.domainRole !== 'restricted') return true;

  return (
    profile.domainRole !== 'restricted' && profile.domainRole !== 'standard'
  );
};

const ShareResource = ({
  resource,
  resourceType,
  onShare,
  onUnshare,
}: ShareResourceProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  // Selectors

  const otherDomainProfiles = useSelector(selectOtherDomainProfiles);
  const profileResults = useSelector(selectSharingProfileResults);
  const sortOptions = useSelector(selectSharingSortOptions);
  const searchQuery = useSelector(selectSharingSearchQuery);
  const { data: currentUser } = useCurrentUser();

  // State

  const [shareWithProfileId, setShareWithProfileId] = useState<string | null>(
    null,
  );
  const [shareStatus, setShareStatus] = useState<
    '' | 'pending' | 'success' | 'error'
  >('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSortMode, setIsSortMode] = useState<boolean>(false);
  const [selectedACL, setSelectedACL] = useState<ResourceACL | null>(null);
  const [confirmationEl, setConfirmationEl] = useState<Element | null>(null);

  // Memoizers

  const hasSharedProfiles = useMemo(() => {
    return otherDomainProfiles.some((profile) =>
      canEditResource(profile, resource),
    );
  }, [otherDomainProfiles, resource]);

  const sharedWithProfiles = useMemo(() => {
    return profileResults.filter((profile) =>
      canEditResource(profile, resource),
    );
  }, [profileResults, resource]);

  // Callbacks

  const shareResource = useCallback(async () => {
    if (!shareWithProfileId) return;
    setShareStatus('pending');

    try {
      await onShare(shareWithProfileId);
      setShareStatus('success');
      setShareWithProfileId(null);
    } catch (err) {
      setShareStatus('error');
    }
  }, [onShare, shareWithProfileId]);

  const unshareResource = useCallback(async () => {
    if (!selectedACL) return;

    setSelectedACL(null);
    setConfirmationEl(null);

    try {
      await onUnshare(selectedACL);
    } catch (err) {
      console.error(err);
    }
  }, [selectedACL, onUnshare]);

  const toggleSort = useCallback(
    (property: SortProfileOptions['property']) => {
      let direction: SortProfileOptions['direction'] = 'asc';

      if (property === sortOptions.property) {
        direction = sortOptions.direction === 'asc' ? 'desc' : 'asc';
      }

      dispatch(actions.setSortOptions({ property, direction }));
      setIsSortMode(false);
    },
    [dispatch, sortOptions],
  );

  const setSearchQuery = useCallback(
    (value: string) => {
      dispatch(actions.setSearchQuery(value));
    },
    [dispatch],
  );

  const closeConfirmation = useCallback(() => {
    setSelectedACL(null);
    setConfirmationEl(null);
  }, [setSelectedACL, setConfirmationEl]);

  // Side-effects

  // Reset search query when exiting search mode.
  useEffect(() => {
    if (isSearchMode) return;
    dispatch(actions.setSearchQuery(''));
  }, [isSearchMode, dispatch]);

  // Render

  const getResourceACL = (profileId: string) => {
    return resource.r.resourceACLs.find(
      (acl) => acl.grantProfileId === profileId,
    );
  };

  let sortLabel = 'Sort';
  if (sortOptions.property === 'name') {
    sortLabel = `${sortLabel}: Name`;
  } else if (sortOptions.property === 'domainRole') {
    sortLabel = `${sortLabel}: Role`;
  }

  const isSortByName = sortOptions.property === 'name';
  const isSortByRole = sortOptions.property === 'domainRole';
  const isReverseSort = sortOptions.direction === 'desc';

  return (
    <>
      <Form onSubmit={shareResource}>
        <Column doubleMargin>
          <div>
            <Text as="label" htmlFor="shareWithProfileId" className="mb-1">
              Share with Account
            </Text>
            <div className="flex gap-2">
              <div className="flex-[6]">
                <Select
                  id="shareWithProfileId"
                  selectedKey={shareWithProfileId ?? ''}
                  onSelectionChange={(val) => {
                    setShareWithProfileId(val as string);
                  }}
                  placeholder="Select account"
                >
                  <SelectItem></SelectItem>
                  {otherDomainProfiles
                    .filter((profile) =>
                      filterStandardAndRestrictedUsers(profile, currentUser),
                    )
                    .map((profile) => (
                      <SelectItem
                        key={profile.id}
                        id={profile.id}
                        isDisabled={canEditResource(profile, resource)}
                      >
                        {profile.name}
                      </SelectItem>
                    ))}
                </Select>
              </div>
              <div className="flex-[4]">
                <Button
                  size="medium"
                  variant="primary"
                  type="submit"
                  isDisabled={!shareWithProfileId || shareStatus === 'pending'}
                  fullWidth
                  className="min-h-10 h-10"
                >
                  Share
                </Button>
              </div>
            </div>
          </div>
          {hasSharedProfiles && (
            <div>
              <InputLabel>Accounts with edit or higher access</InputLabel>

              <ActionBar className={classes.actions}>
                <ActionBar.Select
                  icon={<FilterListIcon />}
                  label={sortLabel}
                  open={isSortMode}
                  onOpen={setIsSortMode}
                >
                  <ActionBar.SelectOption
                    icon={
                      isSortByName && isReverseSort ? (
                        <SwapVertReverseIcon />
                      ) : (
                        <SwapVertIcon />
                      )
                    }
                    label="Name"
                    selected={isSortByName}
                    onClick={() => toggleSort('name')}
                  />
                  <ActionBar.SelectOption
                    icon={
                      isSortByRole && isReverseSort ? (
                        <SwapVertReverseIcon />
                      ) : (
                        <SwapVertIcon />
                      )
                    }
                    label="Role"
                    selected={isSortByRole}
                    onClick={() => toggleSort('domainRole')}
                  />
                </ActionBar.Select>

                <Spacer />

                {isSearchMode ? (
                  <ActionBar.Input
                    autoFocus
                    label="Search"
                    maxWidth={145}
                    icon={
                      <ActionBar.Action
                        icon={<CloseIcon />}
                        onClick={() => setIsSearchMode(false)}
                      />
                    }
                    value={searchQuery}
                    onChange={(value) => setSearchQuery(value)}
                  />
                ) : (
                  <ActionBar.Action
                    icon={<SearchIcon />}
                    onClick={() => setIsSearchMode(true)}
                  />
                )}
              </ActionBar>

              {sharedWithProfiles.map((profile) => {
                const acl = getResourceACL(profile.id);
                return (
                  <Row
                    key={profile.id}
                    halfMargin
                    className={cn(classes.acl, 'mb-1 py-1')}
                  >
                    <div className={classes.aclName}>{profile.name}</div>
                    <div className={classes.aclRole}>
                      {domainRoleLabels[profile.domainRole]}
                    </div>
                    <button
                      className={classes.remove}
                      disabled={!acl}
                      onClick={(event) => {
                        if (!acl) return;
                        setSelectedACL(acl);
                        setConfirmationEl(event.currentTarget);
                      }}
                    >
                      <HighlightOffIcon />
                    </button>
                  </Row>
                );
              })}
            </div>
          )}
        </Column>
      </Form>

      <Popover
        variant="alert"
        open={!!selectedACL}
        anchorEl={confirmationEl}
        onClose={closeConfirmation}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Column>
          <Row halfMargin>
            <AlertIcon color="warning" />
            <Text className="text-sm">
              Removing access will no longer allow{' '}
              <strong>{selectedACL?.r.grantProfile?.name}</strong> to make
              changes to this {resourceType}.
            </Text>
          </Row>
          <Row>
            <Spacer />
            <Button onPress={closeConfirmation} fullWidth>
              Cancel
            </Button>
            <Button fullWidth variant="caution" onPress={unshareResource}>
              Remove Access
            </Button>
          </Row>
        </Column>
      </Popover>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    popover: {
      '& $popoverPaper': {
        padding: theme.spacing(2),
        maxWidth: 300,
      },
    },

    popoverPaper: {},

    acl: {
      alignItems: 'center',
    },

    aclName: {
      ...textTruncate(),
      flex: 1,
      fontSize: theme.fontSizes.lg,
      letterSpacing: 0.15,
    },

    aclRole: {
      flex: 1,
      fontSize: theme.fontSizes.md,
      color: 'rgba(0, 0, 0, 0.45)',
      letterSpacing: 0.1,
    },

    remove: {
      ...buttonReset(),
      display: 'flex',
      color: theme.palette.text.secondary,
      opacity: 0.6,

      '&:hover': {
        opacity: 1,
      },

      '&:disabled': {
        opacity: 0,
        cursor: 'default',
      },
    },

    actions: {
      marginBottom: theme.spacing(1),
    },
  });
});

export default ShareResource;
